import * as React from "react";
import "url-polyfill"; // side effect - loads URL Polyfill for IE.
import styled from "styled-components";
import { navTo } from "@origin-digital/event-dispatcher";
import { Logo } from "../Svg/Logo";
import { BREAK_POINTS } from "../../consts/style";
import { MobileNav } from "./mobile/MobileNav";
import { AccountNav } from "./AccountNav/AccountNav";
import { DropdownMenu } from "./DropdownMenu/DropdownMenu";
import { IMenuItem } from "./IMenuItem";
import { BorderedNavLink } from "./NavLink";
import { UNKNOWN_MENU_INDEX } from "./const";
import {
  addTrackingToNavigation,
  defaultPlanBtnContent,
  getLogoLink,
  locateMenuIndexByHeuristic,
  locateMenuIndexForPath,
  plansBtnContent,
} from "./navService";

const Nav = styled.nav`
  background: white;
  max-height: 64px;
  position: relative;
  border-bottom: 1px solid ${(p) => p.theme.colors.grey200};
  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    max-height: 136px;
    height: 136px;
  }
`;

export const SmallLogoLink = styled(BorderedNavLink)`
  padding: 7px;
  top: 0;
  left: 0;
  width: 64px;
  height: 64px;
  float: left;
  position: relative;

  & > svg {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);
    padding: 8px;
    box-sizing: border-box;
  }
`;

export const LogoLink = styled(SmallLogoLink)`
  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    padding: 14px;
    height: 136px;
    width: 101px;
    & > svg {
      padding: 14px;
      height: 100%;
      width: 100%;
    }
  }
`;

const DesktopNav = styled.div`
  display: none;
  width: 100%;
  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    display: block;
  }
`;

const UpperNavContainer = styled.div`
  border-bottom: 1px solid ${(p) => p.theme.colors.grey100};
  height: 48px;
`;

const UpperNavContainer2 = styled(UpperNavContainer)`
  flex-shrink: 0;
  @media (min-width: ${BREAK_POINTS.MIN.LG}) {
    flex-basis: 450px;
  }
`;

const AccountLinks = styled.div``;
const NavContainer = styled.div``;
const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FlexFillLrg = styled(UpperNavContainer)`
  display: none;
  @media (min-width: ${BREAK_POINTS.MIN.LG}) {
    display: block;
    flex-grow: 1;
  }
`;

const FlexFill = styled(UpperNavContainer)`
  display: block;
  flex-grow: 1;
`;

export interface NavigationProps {
  menuItems: IMenuItem[];
  activeTopMenuIndex: number;
  activeDropdownMenuIndex: number;
}

export interface IPlansBtnContent {
  label: string;
  link: string;
}

interface State {
  trackedMenus: any;
  calculatedActiveTopMenuIndex: number;
  calculatedActiveDropdownMenuIndex: number;
  plansBtnContent: IPlansBtnContent;
}

export class Navigation extends React.Component<NavigationProps, State> {
  constructor(props: NavigationProps) {
    super(props);

    this.state = {
      trackedMenus: addTrackingToNavigation(props.menuItems),
      calculatedActiveTopMenuIndex: props.activeTopMenuIndex,
      calculatedActiveDropdownMenuIndex: props.activeDropdownMenuIndex,
      plansBtnContent: defaultPlanBtnContent,
    };
  }

  componentDidMount(): void {
    // attempt to locate the active top level menu item and the active sub menu item beneath it.

    // eslint-disable-next-line react/no-access-state-in-setstate
    let calculatedActiveDropdownMenuIndex = this.state
      .calculatedActiveDropdownMenuIndex;
    // eslint-disable-next-line react/no-access-state-in-setstate
    let calculatedActiveTopMenuIndex = this.state.calculatedActiveTopMenuIndex;

    // attempt to locate top level index in given menu for the current path
    if (calculatedActiveTopMenuIndex === UNKNOWN_MENU_INDEX) {
      calculatedActiveTopMenuIndex = locateMenuIndexForPath(
        this.props.menuItems,
        window.location.pathname
      );

      // if not found, use some dodgy hard-coded 'hueristics' to try again.
      if (calculatedActiveTopMenuIndex === UNKNOWN_MENU_INDEX) {
        calculatedActiveTopMenuIndex = locateMenuIndexByHeuristic(
          this.props.menuItems,
          window.location.pathname
        );
      }

      // if we found one, fetch index for the sub menu
      if (calculatedActiveTopMenuIndex !== UNKNOWN_MENU_INDEX) {
        calculatedActiveDropdownMenuIndex = locateMenuIndexForPath(
          this.props.menuItems[calculatedActiveTopMenuIndex].children,
          window.location.pathname
        );
      }
    }

    // if no top level menu item, default to first.
    if (calculatedActiveTopMenuIndex === UNKNOWN_MENU_INDEX) {
      calculatedActiveTopMenuIndex = 0;
      calculatedActiveDropdownMenuIndex = UNKNOWN_MENU_INDEX;
    }

    this.setState({
      calculatedActiveTopMenuIndex,
      calculatedActiveDropdownMenuIndex,
      plansBtnContent: plansBtnContent(window.location.pathname),
    });
  }

  render() {
    const logoLinkHref = getLogoLink(
      this.state.trackedMenus,
      this.state.calculatedActiveTopMenuIndex
    );
    return (
      <Nav>
        <DesktopNav data-comments="DesktopNav">
          <LogoLink
            trackingType="navDesktopLogoLink"
            data-id="desktop-logolink"
            href="#"
            onClick={() => navTo.home()}
          >
            <Logo id="desktop-nav" />
          </LogoLink>
          <FlexContainer>
            <FlexFillLrg />
            <NavContainer>
              <UpperNavContainer>
                {this.state.trackedMenus.map((topMenu, index) => (
                  <BorderedNavLink
                    data-id={topMenu.tracking}
                    trackingType="navDesktopTopLink"
                    key={index}
                    href={topMenu.link}
                    className={
                      index === this.state.calculatedActiveTopMenuIndex
                        ? "active"
                        : ""
                    }
                  >
                    <span>{topMenu.title}</span>
                  </BorderedNavLink>
                ))}
              </UpperNavContainer>
              <DropdownMenu
                menuItems={
                  this.state.trackedMenus[
                    this.state.calculatedActiveTopMenuIndex ==
                    UNKNOWN_MENU_INDEX
                      ? 0
                      : this.state.calculatedActiveTopMenuIndex
                  ].children || []
                }
                activeMenuIndex={this.state.calculatedActiveDropdownMenuIndex}
              />
            </NavContainer>
            <FlexFill />
            <UpperNavContainer2 data-comment="desktop-nav-upper-2">
              <AccountLinks>
                <AccountNav
                  text={this.state.plansBtnContent.label}
                  href={this.state.plansBtnContent.link}
                />
              </AccountLinks>
            </UpperNavContainer2>
          </FlexContainer>
        </DesktopNav>
        <MobileNav
          logoLinkHref={logoLinkHref}
          menuItems={this.state.trackedMenus}
        />
      </Nav>
    );
  }
}
